import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Button,
  Typography
} from '@material-ui/core'
import { Paperclip, Trash2 } from 'lucide-react' // Seguindo o padrão DepartmentModal
import { head } from 'lodash'

import { i18n } from '../../translate/i18n'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { getBackendURL } from '../../services/config'

import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import ConfirmationModal from '../ConfirmationModal'
import { InputField } from '../InputField'
import { InputSwitch } from '../InputSwitch'
import { InputTextArea } from '../InputTextArea'
import { useStyles } from './styles'

const AnnouncementSchema = Yup.object().shape({
  title: Yup.string().required('Obrigatório'),
  text: Yup.string().required('Obrigatório'),
  tipo: Yup.string().required('Obrigatório')
})

const AnnouncementModal = ({ open, onClose, announcementId, reload }) => {
  const classes = useStyles()
  const attachmentFile = useRef(null)

  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [attachment, setAttachment] = useState(null)
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false)
  const [mediaDeleted, setMediaDeleted] = useState(false)

  const initialState = useMemo(
    () => ({
      title: '',
      text: '',
      linkout: '',
      priority: 1,
      status: true,
      tipo: '2',
      showButtonForRoles: '',
      isGlobal: true,
      isConnectionAnnouncement: false,
      mediaPath: null,
      mediaName: ''
    }),
    []
  )

  const [announcement, setAnnouncement] = useState(initialState)

  useEffect(() => {
    if (!open) return
    const fetchAnnouncement = async () => {
      if (!announcementId) {
        setAnnouncement(initialState)
        setMediaDeleted(false)
        setAttachment(null)
        return
      }
      try {
        const { data } = await api.get(`/announcements/${announcementId}`)
        setAnnouncement({ ...initialState, ...data, priority: 1 })
      } catch (err) {
        toastError(err)
      }
    }
    fetchAnnouncement()
  }, [announcementId, open, initialState])

  const handleClose = () => {
    setAttachment(null)
    setMediaDeleted(false)
    onClose()
  }

  const handleAttachmentFile = (e, setFieldValue) => {
    const file = head(e.target.files)
    if (file) {
      setAttachment(file)
      setFieldValue('attachmentFile', file)
    }
  }

  const getMediaUrl = () => {
    if (attachment) {
      return URL.createObjectURL(attachment)
    }
    if (announcement?.mediaPath && !mediaDeleted) {
      return announcement.mediaPath.startsWith('http')
        ? announcement.mediaPath
        : `${getBackendURL()}/public/${announcement.mediaPath}`
    }
    return null
  }

  const handleSaveAnnouncement = async values => {
    const announcementData = {
      ...values,
      priority: 1,
      isGlobal: true,
      tipo: '2'
    }
    try {
      let currentId = announcementId

      // Se a mídia foi marcada para exclusão
      if (mediaDeleted && announcementId) {
        await api.delete(`/announcements/${announcementId}/media-upload`)
      }

      if (announcementId) {
        await api.put(`/announcements/${announcementId}`, announcementData)
      } else {
        const { data } = await api.post('/announcements', announcementData)
        currentId = data.id
      }

      if (attachment && currentId) {
        const formData = new FormData()
        formData.append('file', attachment)
        await api.post(`/announcements/${currentId}/media-upload`, formData)
      }

      toast.success(i18n.t('announcements.toasts.success'))
      if (typeof reload === 'function') reload()
      handleClose()
    } catch (err) {
      toastError(err)
    }
  }

  const deleteMedia = () => {
    if (attachment) {
      setAttachment(null)
      if (attachmentFile.current) attachmentFile.current.value = null
    }
    if (announcement.mediaPath) {
      setMediaDeleted(true)
    }
    setConfirmationOpen(false)
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.dialogTitle}>
          {announcementId
            ? i18n.t('announcements.dialog.edit')
            : i18n.t('announcements.dialog.add')}
        </DialogTitle>

        <Formik
          initialValues={announcement}
          enableReinitialize
          validationSchema={AnnouncementSchema}
          onSubmit={handleSaveAnnouncement}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <ConfirmationModal
                  title="Excluir Anexo"
                  open={confirmationOpen}
                  onClose={() => setConfirmationOpen(false)}
                  onConfirm={deleteMedia}
                >
                  Tem certeza que deseja remover este anexo?
                </ConfirmationModal>

                <Grid spacing={2} container>
                  <Grid xs={12} item>
                    <InputField
                      label={i18n.t('announcements.dialog.form.title')}
                      name="title"
                      error={touched.title && !!errors.title}
                      helperText={touched.title && errors.title}
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <InputTextArea
                      label={i18n.t('announcements.dialog.form.text')}
                      name="text"
                      error={touched.text && !!errors.text}
                      helperText={touched.text && errors.text}
                      multiline
                      minRows={3}
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <InputField
                      label="Link"
                      name="linkout"
                      placeholder="https://www.google.com"
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <InputSwitch
                      label={i18n.t('announcements.dialog.form.status')}
                      name="status"
                      checked={values.status}
                      onChange={e => setFieldValue('status', e.target.checked)}
                    />
                    {/* <InputSwitch
                      label="Anúncio de Conexão"
                      name="isConnectionAnnouncement"
                      checked={values.isConnectionAnnouncement}
                      onChange={e =>
                        setFieldValue(
                          'isConnectionAnnouncement',

                          e.target.checked
                        )
                      }
                    /> */}
                  </Grid>

                  <Grid item xs={12} className={classes.attachmentContainer}>
                    <Grid item xs={4} className={classes.buttonsAttachmentFile}>
                      {getMediaUrl() && !mediaDeleted ? (
                        <div
                          style={{ textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => setImagePreviewOpen(true)}
                        >
                          <img
                            src={getMediaUrl()}
                            alt="Anexo Preview"
                            className={classes.attachmentPreviewImage}
                          />
                        </div>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.attachmentNotFile}
                        >
                          <Paperclip size={20} />
                          Sem anexo
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={8} className={classes.buttonsAttachment}>
                      <div className={classes.buttonsBoxAttachment}>
                        <input
                          type="file"
                          ref={attachmentFile}
                          style={{ display: 'none' }}
                          accept=".jpg,.jpeg,.png,.bmp"
                          onChange={e => handleAttachmentFile(e, setFieldValue)}
                        />
                        <Button
                          onClick={() => attachmentFile.current.click()}
                          className={classes.buttonFile}
                        >
                          <Paperclip size={16} style={{ marginRight: 8 }} />
                          Anexar
                        </Button>
                        <Grid item xs={12} className={classes.buttonsActions}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                              marginTop: '8px',
                              display: 'block'
                            }}
                          >
                            Formatos: JPG, JPEG, PNG, BMP
                          </Typography>
                          {(attachment ||
                            (announcement.mediaPath && !mediaDeleted)) && (
                              <IconButton
                                onClick={() => setConfirmationOpen(true)}
                                className={classes.iconButtonDelete}
                              >
                                <Trash2 size={20} />
                              </IconButton>
                            )}
                        </Grid>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <ButtonSecondary
                  onClick={handleClose}
                  disabled={isSubmitting}
                  label="Cancelar"
                />
                <ButtonPrimary
                  type="submit"
                  disabled={isSubmitting}
                  label="Salvar"
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* MODAL DE PREVIEW DA IMAGEM */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
      >
        <DialogContent>
          <img
            src={getMediaUrl()}
            alt="Preview"
            style={{ width: '100%', height: 'auto' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AnnouncementModal
